import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Plus, Trash2, ListChecks, CheckCircle2, Repeat } from "lucide-react";

export default function TasksPage() {
  const { user, isManager } = useAuth();
  const [items, setItems] = useState([]);
  const [users, setUsers] = useState([]);
  const [locations, setLocations] = useState([]);
  const [tab, setTab] = useState("open");
  const [dialog, setDialog] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", assigned_user_ids: [], assigned_role: null, location_id: null, due_date: "", recurrence: "none", priority: "normal", is_qm: false });

  const load = async () => {
    const [r1, r2, r3] = await Promise.all([
      api.get("/tasks"),
      isManager ? api.get("/users") : Promise.resolve({ data: [] }),
      api.get("/locations"),
    ]);
    setItems(r1.data);
    setUsers(r2.data);
    setLocations(r3.data);
  };
  useEffect(() => { load(); }, [isManager]);

  const filtered = items.filter((t) => tab === "all" || (tab === "open" ? t.status !== "done" : t.status === "done"));

  const create = async () => {
    try { await api.post("/tasks", form); toast.success("Angelegt"); setDialog(false); await load(); } catch (err) { toast.error("Fehler"); }
  };

  const toggle = async (t) => {
    const next = t.status === "done" ? "open" : "done";
    try { await api.post(`/tasks/${t.id}/status`, { status: next }); await load(); } catch (err) { toast.error("Fehler"); }
  };

  const del = async (id) => { if (!window.confirm("Löschen?")) return; await api.delete(`/tasks/${id}`); await load(); };

  return (
    <div className="space-y-6" data-testid="tasks-root">
      <div className="flex justify-between items-center">
        <div>
          <div className="overline">Team</div>
          <h2 className="font-heading text-2xl sm:text-3xl font-bold">Aufgaben</h2>
        </div>
        {isManager && <Button data-testid="task-add-btn" onClick={() => setDialog(true)} className="rounded-full"><Plus className="w-4 h-4 mr-2" /> Aufgabe</Button>}
      </div>

      <div className="flex gap-2">
        {["open", "done", "all"].map((t) => (
          <Button key={t} data-testid={`task-tab-${t}`} variant={tab === t ? "default" : "outline"} size="sm" onClick={() => setTab(t)}>
            {t === "open" ? "Offen" : t === "done" ? "Erledigt" : "Alle"}
          </Button>
        ))}
      </div>

      <div className="space-y-2">
        {filtered.map((t) => (
          <Card key={t.id} data-testid={`task-${t.id}`} className="border-border/60 card-hover">
            <CardContent className="p-3 flex items-start gap-3">
              <Checkbox data-testid={`task-check-${t.id}`} checked={t.status === "done"} onCheckedChange={() => toggle(t)} />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`font-semibold ${t.status === "done" ? "line-through text-slate-400" : ""}`}>{t.title}</span>
                  {t.priority !== "normal" && <Badge variant="outline" className="text-xs">{t.priority}</Badge>}
                  {t.recurrence !== "none" && <Badge variant="secondary" className="text-xs"><Repeat className="w-3 h-3 mr-1" />{t.recurrence}</Badge>}
                  {t.is_qm && <Badge className="bg-blue-100 text-blue-800 hover:bg-blue-100 text-xs">QM</Badge>}
                </div>
                {t.description && <p className="text-sm text-slate-500 mt-0.5">{t.description}</p>}
                {t.due_date && <div className="text-xs text-slate-400 mt-1">Fällig: {t.due_date}</div>}
              </div>
              {isManager && <Button size="icon" variant="ghost" onClick={() => del(t.id)}><Trash2 className="w-4 h-4 text-rose-600" /></Button>}
            </CardContent>
          </Card>
        ))}
        {filtered.length === 0 && <div className="text-center text-slate-500 py-12">Keine Aufgaben</div>}
      </div>

      <Dialog open={dialog} onOpenChange={setDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>Neue Aufgabe</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Input placeholder="Titel" data-testid="task-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
            <Textarea placeholder="Beschreibung" data-testid="task-desc" rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Fälligkeitsdatum</Label><Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} /></div>
              <div>
                <Label>Wiederholung</Label>
                <Select value={form.recurrence} onValueChange={(v) => setForm({ ...form, recurrence: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="none">Einmalig</SelectItem><SelectItem value="daily">Täglich</SelectItem><SelectItem value="weekly">Wöchentlich</SelectItem><SelectItem value="monthly">Monatlich</SelectItem></SelectContent>
                </Select>
              </div>
              <div>
                <Label>Priorität</Label>
                <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="normal">Normal</SelectItem><SelectItem value="important">Wichtig</SelectItem><SelectItem value="urgent">Dringend</SelectItem></SelectContent>
                </Select>
              </div>
              <div>
                <Label>Position (Rolle)</Label>
                <Input placeholder="z.B. PTA" value={form.assigned_role || ""} onChange={(e) => setForm({ ...form, assigned_role: e.target.value })} />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Checkbox checked={form.is_qm} onCheckedChange={(v) => setForm({ ...form, is_qm: !!v })} />
              <Label className="cursor-pointer">QM-Aufgabe (Verfallsdaten, Temperatur, Kalibrierung)</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog(false)}>Abbrechen</Button>
            <Button data-testid="task-save" onClick={create}>Speichern</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
